import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import { FC, MouseEventHandler, useMemo } from 'react';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { DataObjectOutputType } from '../../types';
import { AlgorithmHeader } from '../AlgorithmHeader';
import translations from './AlgorithmSources.translations.json';
import { AlgorithmSourcesConfidentialityRule } from './confidentialityRule/AlgorithmSourcesConfidentialityRule';
import { AlgorithmSourceTag } from './AlgorithmSourceTag';
import { useGetAlgorithmSourcesQuery } from './query/useGetAlgorithmSourcesQuery';
import { AlgorithmSourceFormDataType } from './schema/types';

const { addTranslations, useTranslation } = i18nNsFactory('algorithm-sources');
addTranslations(translations);

const getTypeSortValue = (source: AlgorithmSourceFormDataType) => {
  if (source.sourceType === 'DB') {
    return 0;
  } else {
    switch (source.sourceType) {
      case 'IN':
        return 1;
      case 'OUT':
      case 'OUTPUT':
        return 2;
      case 'INTERMEDIATE':
        return 3;
      case 'REPORT':
      default:
        return 4;
    }
  }
};

export interface AlgorithmSourcesProps {
  algorithmId: number;
  onEditButtonClick?: MouseEventHandler<HTMLButtonElement>;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  disableButtons?: boolean;
  hasError?: boolean;
  isLoading?: boolean;
}

export const AlgorithmSources: FC<AlgorithmSourcesProps> = (props) => {
  const { algorithmId, isLoading, ...rest } = props;
  const { t } = useTranslation();

  const query = useGetAlgorithmSourcesQuery({
    id: algorithmId,
  });
  const { data: algorithmSources } = query;
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const sources = useMemo(
    () =>
      (algorithmSources?.sources || []).toSorted((a, b) => {
        const typeA = getTypeSortValue(a);
        const typeB = getTypeSortValue(b);
        if (typeA === typeB) {
          if (a.sourceCode !== b.sourceCode) {
            return a.sourceCode.localeCompare(b.sourceCode);
          }
          if (a.numberOfPreviousPeriod !== b.numberOfPreviousPeriod) {
            return (a.numberOfPreviousPeriod || 0) >
              (b.numberOfPreviousPeriod || 0)
              ? 1
              : -1;
          }
          if (a.alias !== b.alias) {
            return (a.alias || '').localeCompare(b.alias || '');
          }
        }

        return typeA - typeB;
      }),
    [algorithmSources?.sources],
  );

  return (
    <AlgorithmHeader
      algorithmId={algorithmId}
      id="algorithm-sources"
      title={t('title')}
      count={query.isLoading ? undefined : sources?.length}
      isLoading={isLoading || query.isLoading ? 'manage' : undefined}
      hideExpand={!sources?.length}
      {...rest}
    >
      {Boolean(sources?.length) && (
        <Flex vertical gap="large">
          <Flex wrap="wrap" gap="small">
            {(sources || []).map((source, index) => (
              <AlgorithmSourceTag key={index} algorithmSource={source} />
            ))}
          </Flex>
          {dataObjectDescriptionVersion.outputTypeCode ===
            DataObjectOutputType.REPORT && (
            <AlgorithmSourcesConfidentialityRule
              algorithmSources={algorithmSources!}
            />
          )}
        </Flex>
      )}
    </AlgorithmHeader>
  );
};
